import React, { useEffect, useState } from 'react'
import { useNavigate, createSearchParams } from 'react-router-dom'
import ActionArea from '../../components/ActionArea'
import Head from '../../components/Head'
import List from '../../components/List'
import SmartSearch from '../../components/UI/SmartSearch'
import { useLoading } from '../../context/LoadingProvider/useLoading'
import { notificationError } from '../../services/notification'
import { getPermission } from '../../services/permisssion'
import { deleteGameById, getPulltabGamesList } from '../../services/pulltabGames'
import permissionType from '../../utils/permissionType'
import {
  EditOutlined,
  DeleteOutlined,
  EyeOutlined
} from '@ant-design/icons';
import Button from '../../components/UI/Button'
import { Modal } from 'antd'
import { numberToCurrency } from '../../utils/objectUtils'
import { sendSystemLog, useAuth } from '../../context/AuthProvider/useAuth'
import { compareNegativeCurrency, compareNumbers, stringCompare } from '../../utils/sorters'
import {IDeckGame} from "../DeckStatus/types";

type PulltabGame = {
    id: string;
    title: string;
    form: string;
    version: string;
    wager: string;
    ticketCount: string;
    formChecksum: string;
    autoLoad: string;
    autoClose: string;
    autoCloseLimit: string;
}

type pulltabGamesList = PulltabGame[]

function PulltabGames(props:{accessLevel: string}) {
  const {canEdit} = useAuth()
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const name = urlParams.get('name')
  const idParam = urlParams.get('idParam')
  const history = useNavigate()
  const loading = useLoading()
  const [search, setSearch] = useState('')
  const [validSearch, setValidSearch] = useState(false)
  const [params, setParams] = useState({
    id: '',
    type: '',
    name: ''
  })
  const [pulltabGamesList, setPulltabGamesList] = useState<pulltabGamesList>([])
  const [isModalVisible, setIsModalVisible] = useState({
    visible: false,
    idToDelete: '',
    form: ''
  })
  const log = {
    log_type: 'visited',
    log_object: 'gamelist'
  }
  

  
  const onRefresh = async () => {
    loading.addLoading()
    const roles = await getPulltabGamesList({id: params.id})
    setPulltabGamesList(roles.list)
    loading.removeLoading()
  }
  
  useEffect(() => {
    sendSystemLog(log);
  }, [])
  
  useEffect(() => {
    (async () => {
      loading.addLoading()
      if(name && idParam) {
        setParams({
          ...params,
          name,
          id:idParam
        })
        setSearch(name)
        const roles = await getPulltabGamesList({id: idParam})
        setPulltabGamesList(roles.list)
      }
      loading.removeLoading()
    })()
  }, [])

  const getActionButtons = (game: any) => {
    return (
      <div className="roles__button-actions" style={{display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap'}}>
        <Button
          disabled={!canEdit }
          onClick={() => history({
            pathname: `../games/settings/${game.id}`,
            search: createSearchParams({
              idParam: params.id,
              name: params.name
          }).toString()
          })}
          type='primary'
          style={{ backgroundColor: '#2fa4e7' }}
        >
          <EditOutlined style={{ fontSize: '16px', color: '#FFF' }} />
        </Button>
        <Button
          disabled={!canEdit}
          onClick={() => {
            setIsModalVisible({visible: true, idToDelete: game.id, form: game.form})
          }}
          type='primary'
          style={{ backgroundColor: '#d41d24' }}
        >
          <DeleteOutlined style={{ fontSize: '16px', color: '#FFF' }} />
        </Button>
      </div>
    )
  }

  const deleteGame = async () => {
      if (!isModalVisible.idToDelete) return notificationError('Could not find game')
      const deleted = await deleteGameById({id: isModalVisible.idToDelete})

      if (deleted) {
          const res = pulltabGamesList.filter(obj => obj.id !== isModalVisible.idToDelete);
          setPulltabGamesList(res)
      }
      
      setSearch('')
      setIsModalVisible({visible: false, idToDelete: '', form: ''})
  }

  const normalizeReponse = (value?: string): string => {
    return !value || value === 'No' ? 'No' : 'Yes'
  }

  return (
    <div className="pulltab-games">
      <Modal 
        title={`Are you sure you want to remove form ${isModalVisible.form}? This will affect all decks in production for this form.`}
        open={isModalVisible.visible}
        onOk={() => deleteGame()} 
        onCancel={() => setIsModalVisible({...isModalVisible, visible: false})} 
      />
      <Head 
        title="Roles" 
        buttonAction={() => history(`../games/upload/`)} 
        buttonText="Add or Update Game" 
        disabled={!canEdit} 
      />
      <ActionArea buttonAction={onRefresh} buttonText={'Search'} disabled={!validSearch}>
        <SmartSearch
          value={search}
          triggerWith={name}
          onChange={(e: string) => setSearch(e)}
          onValidSearch={setValidSearch}
          label='Smart Search'
          searchBy={['listgames']}
          onObjValueChange={(e: any) => setParams(e)}
          type="listgames"
          status="active"
          placeholder="title or form"
          validOnEmpty
          autoFocus
        />
      </ActionArea>
      <List
          rowClassName={(record, index) => {
              // @ts-ignore
              if(record.autoClose === "No") return 'list-component__yellow'
              return ''
          }}
        rows={pulltabGamesList}
        columns={[
          {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
            width: 100,
            render: (value: string) => value,
            sorter: (a: PulltabGame, b: PulltabGame) => stringCompare(a.title, b.title)
          },
          {
            title: 'Form',
            dataIndex: 'form',
            key: 'form',
            width: 80,
            render: (value: string) => value,
            sorter: (a: PulltabGame, b: PulltabGame) => stringCompare(a.form, b.form)
          },
          {
            title: 'Version',
            dataIndex: 'version',
            key: 'version',
            width: 100,
            render: (value: string) => value,
            sorter: (a: PulltabGame, b: PulltabGame) => stringCompare(a.version, b.version)
          },
          {
            title: 'Wager',
            dataIndex: 'wager',
            key: 'wager',
            width: 80,
            render: (value: string) => value,
            sorter: (a: PulltabGame, b: PulltabGame) => compareNumbers(parseInt(a.wager), parseInt(b.wager))
          },
          {
            title: 'Ticket Count',
            dataIndex: 'ticketCount',
            key: 'ticketCount',
            width: 80,
            render: (value: string) => value,
            sorter: (a: PulltabGame, b: PulltabGame) => stringCompare(a.ticketCount, b.ticketCount)
          },
          {
            title: 'Checksum',
            dataIndex: 'formChecksum',
            key: 'formChecksum',
            width: 100,
            render: (value: string) => value,
            sorter: (a: PulltabGame, b: PulltabGame) => stringCompare(a.formChecksum, b.formChecksum)
          },
          {
            title: 'Auto Create',
            dataIndex: 'autoLoad',
            key: 'autoLoad',
            width: 100,
            render: (value: string) => normalizeReponse(value),
            sorter: (a: PulltabGame, b: PulltabGame) => stringCompare(normalizeReponse(a.formChecksum), normalizeReponse(b.formChecksum))
          },
          {
            title: 'Auto Close',
            dataIndex: 'autoClose',
            key: 'autoClose',
            width: 100,
            render: (value: string) => normalizeReponse(value),
            sorter: (a: PulltabGame, b: PulltabGame) => stringCompare(normalizeReponse(a.autoClose), normalizeReponse(b.autoClose))
          },
          {
            title: 'Auto Close Limit',
            dataIndex: 'autoCloseLimit',
            key: 'autoCloseLimit',
            width: 75,
            render: (value: string) => value ? numberToCurrency(value) : '--',
            sorter: (a: PulltabGame, b: PulltabGame) => compareNegativeCurrency(a.autoCloseLimit, b.autoCloseLimit)
          },
          {
            title: 'Actions',
            dataIndex: 'id',
            key: 'id',
            width: 100,
            render: (value: PulltabGame["id"], record: PulltabGame) => getActionButtons(record),
          },
        ]} 
      />
    </div>
  )
}

export default PulltabGames
